import type { QcComparisonOperator } from '@/types/quality';

export interface QcCheckpointCriteria {
  comparisonOperator: QcComparisonOperator | null;
  standardValue: number | null;
  toleranceValue: number | null;
}

const OPERATOR_SYMBOL: Record<string, string> = {
  LessThan: '<', GreaterThan: '>', EqualTo: '=', LessThanOrEqual: '≤', GreaterThanOrEqual: '≥',
};

/** Auto-suggests Pass/Fail for a "Value" checkpoint by checking the entered value against the
 * checkpoint's own StandardValue/ComparisonOperator/ToleranceValue. Shared by the QC Inspect popup
 * (QualityControlPage) and the QC Checkpoint Master's definition preview so both always agree on
 * what "pass" means for a given checkpoint. */
export function evaluateCheckpoint(value: number, cp: QcCheckpointCriteria): boolean {
  if (cp.standardValue === null) return true;
  switch (cp.comparisonOperator) {
    case 'LessThan':
      return value < cp.standardValue;
    case 'GreaterThan':
      return value > cp.standardValue;
    case 'LessThanOrEqual':
      return value <= cp.standardValue;
    case 'GreaterThanOrEqual':
      return value >= cp.standardValue;
    case 'EqualTo':
    default:
      return Math.abs(value - cp.standardValue) <= (cp.toleranceValue ?? 0);
  }
}

/** e.g. "= 50 (± 0.5)" or "≤ 12" — null when the checkpoint has no standard value defined. */
export function criteriaLabel(cp: QcCheckpointCriteria): string | null {
  if (cp.standardValue === null) return null;
  const symbol = OPERATOR_SYMBOL[cp.comparisonOperator ?? 'EqualTo'] ?? '=';
  const tolerance = cp.comparisonOperator === 'EqualTo' && cp.toleranceValue ? ` (± ${cp.toleranceValue})` : '';
  return `${symbol} ${cp.standardValue}${tolerance}`;
}
